import type { ReactNode } from 'react';

/**
 * Verbatim body of the "State Farm Gamerhood Chair Giveaway — Official Rules"
 * sweepstakes document, rendered on /sweepstakes (see SweepstakesPage). This
 * is a SEPARATE promotion from the weekly Gamerhood Contest rules in
 * content/weeklyRules.tsx — a Flow Code sweepstakes administered by The
 * Marketing Arm, not the Infinity-run weekly Contest. (The entry platform is
 * still Flow Code / Flowsto.com; only the giveaway's NAME dropped "Flow Code"
 * between the 07.08 v1 and 07.09 v2/v3 documents.)
 *
 * IMPORTANT — this is a 1:1 transcription of the executed document
 * ("State Farm Gamerhood Chair Giveaway Official Rules 07.09.26 v3"). Do not
 * edit, paraphrase, re-order, "improve", or derive the legal text, dates,
 * addresses, or dollar amounts. Three deliberate deviations from verbatim: (1)
 * the Sponsor Privacy Policy URL in §14 is a clickable link (opens in a new
 * tab), its link text the verbatim URL; (2) §16 corrects a source typo
 * ("State Farm Code Gamerhood" → "State Farm Gamerhood"); and (3) §3.i drops a
 * stray ")" that v3 left after the Flowsto link — all per client direction
 * (GHS5-80). See the inline notes. If a revised document lands, re-verify
 * wholesale (word-diff, not spot-check).
 *
 * The document's short-form "Abbreviated Rules" page (footer/banner/social
 * disclosures with [INSERT URL] placeholders) is intentionally NOT included —
 * those are ad-channel disclosures, not the public Official Rules.
 */

/* ─── Shared presentational primitives (match content/weeklyRules.tsx) ─── */

const BODY = "font-['Arial',sans-serif] text-[clamp(13px,3.4vw,15px)] leading-[1.6] text-white/90";

function Para({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={`${BODY} ${className ?? ''}`}>{children}</p>;
}

/** A numbered top-level rules clause: bold lead-in label + body. */
function Clause({ n, label, children }: { n: number; label: string; children: ReactNode }) {
  return (
    <li className="flex gap-[10px]">
      <span className={`${BODY} shrink-0 font-bold`}>{n}.</span>
      <div className="flex flex-col gap-[12px]">
        <div className={BODY}>
          <span className="font-bold">{label}</span> {children}
        </div>
      </div>
    </li>
  );
}

/** A lettered/roman sub-item with a bold marker (used in §3). */
function SubItem({ marker, children }: { marker: string; children: ReactNode }) {
  return (
    <li className="flex gap-[10px]">
      <span className={`${BODY} shrink-0 font-bold`}>{marker}</span>
      <span className={BODY}>{children}</span>
    </li>
  );
}

/* ─── Gamerhood Chair Giveaway — verbatim transcription (v3, 07.09.26) ─── */

export function SweepstakesRules() {
  return (
    <div className="flex w-full flex-col gap-[18px]">
      {/* Title block */}
      <div className="text-center">
        <p className="font-['Orbitron',sans-serif] text-[clamp(14px,3.8vw,18px)] font-bold uppercase leading-[1.3] tracking-[0.04em] text-white">
          State Farm Gamerhood Chair Giveaway
        </p>
        <p className="font-['Orbitron',sans-serif] text-[clamp(13px,3.4vw,16px)] font-bold uppercase leading-[1.3] tracking-[0.04em] text-white">
          Official Rules
        </p>
      </div>

      {/* Header disclosures (bulleted, bold) */}
      <ul className="flex flex-col gap-[10px]">
        <li className="flex gap-[10px]">
          <span className={`${BODY} shrink-0 font-bold`}>&bull;</span>
          <span className={`${BODY} font-bold`}>
            NO PURCHASE OR PAYMENT IS NECESSARY TO ENTER OR WIN. A PURCHASE OF ANY
            KIND DOES NOT INCREASE YOUR CHANCES OF WINNING.
          </span>
        </li>
        <li className="flex gap-[10px]">
          <span className={`${BODY} shrink-0 font-bold`}>&bull;</span>
          <span className={`${BODY} font-bold`}>
            CONTACT WITH A STATE FARM AGENT IS NOT REQUIRED TO ENTER. STATE FARM IS
            IN THE BUSINESS OF INSURANCE AND FINANCIAL SERVICES.
          </span>
        </li>
        <li className="flex gap-[10px]">
          <span className={`${BODY} shrink-0 font-bold`}>&bull;</span>
          <span className={`${BODY} font-bold`}>VOID WHERE PROHIBITED BY LAW.</span>
        </li>
        <li className="flex gap-[10px]">
          <span className={`${BODY} shrink-0 font-bold`}>&bull;</span>
          <span className={`${BODY} font-bold`}>
            ENTRANTS WAIVE THE ABILITY TO BRING CLAIMS IN A CLASS ACTION FORMAT.
          </span>
        </li>
        <li className="flex gap-[10px]">
          <span className={`${BODY} shrink-0 font-bold`}>&bull;</span>
          <span className={`${BODY} font-bold`}>
            THIS SWEEPSTAKES IS IN NO WAY SPONSORED, ENDORSED, ADMINISTERED BY, OR
            ASSOCIATED WITH INSTAGRAM.
          </span>
        </li>
      </ul>

      <Para className="font-bold">
        BY ENTERING (OR OTHERWISE PARTICIPATING IN) THE SWEEPSTAKES, YOU AGREE TO
        THESE OFFICIAL RULES, WHICH ARE A BINDING CONTRACT, SO READ THEM CAREFULLY
        BEFORE ENTERING. WITHOUT LIMITATION, THIS CONTRACT INCLUDES INDEMNITIES TO
        THE SWEEPSTAKES PARTIES FROM YOU AND A LIMITATION OF YOUR RIGHTS AND
        REMEDIES.
      </Para>

      <ol className="flex flex-col gap-[14px]">
        <Clause n={1} label="ELIGIBILITY:">
          The State Farm Gamerhood Chair Giveaway
          (&ldquo;Sweepstakes&rdquo;) is open only to legal residents of the fifty
          (50) United States and the District of Columbia who are eighteen (18)
          years of age or older as of date of entry. Employees, officers and
          directors of State Farm Mutual Automobile Insurance Company
          (&ldquo;Sponsor&rdquo;), State Farm Independent Contractor Insurance and
          Financial Services Agents, The Marketing Arm (&ldquo;Administrator&rdquo;)
          and their respective parent companies, affiliates, subsidiaries,
          distributors, retailers, governors, related entities, partners,
          partnerships, principals, licensees, sponsors, representatives,
          successors and assigns, advertising, promotion, fulfillment,
          administrative, and marketing agencies (collectively, the
          &ldquo;Sweepstakes Parties&rdquo;), their immediate family members and
          those living in the same household as such individuals (whether legally
          related or not) are not eligible to participate in the Sweepstakes or win
          a prize. For purposes of this Sweepstakes, immediate family members are
          defined as spouse, partner, parents, legal guardians, in-laws,
          grandparents, siblings, children and grandchildren and their respective
          spouses, and those living in the same household shall mean people who
          share the same residence at least three (3) months a year, whether
          legally related or not. Void where prohibited by law. All federal, state,
          and local laws and regulations apply. By participating in the
          Sweepstakes, you unconditionally accept and agree to comply with and
          abide by these &ldquo;Official Rules&rdquo; and the decisions of Sponsor,
          including the interpretation of these Official Rules, administration of
          the Sweepstakes, selection of the winners, and Sponsor&rsquo;s exercise
          of discretion, which will be final, non-appealable, and binding in all
          respects.
        </Clause>

        <Clause n={2} label="TIMING:">
          The Sweepstakes begins at or about 12 a.m. Eastern Time
          (&ldquo;ET&rdquo;) on July 10, 2026 and ends at 11:59 p.m. on August 28,
          2026 (the &ldquo;Sweepstakes Period&rdquo;). The designated computer clock
          of the Sponsor is the official time-keeping device in the Sweepstakes.
        </Clause>

        <Clause n={3} label="HOW TO ENTER:">
          To enter the Sweepstakes take the following steps:
          <ol className="mt-[12px] flex flex-col gap-[12px]">
            {/* §3.i: v3 replaced the "[PartnerName]" placeholder with the real
                Flowsto short link (Flowsto.com/vwgwX5gEt) and left a stray ")"
                right after it. The stray paren is removed here per client
                direction (GHS5-80, Ryan Aja 2026-07-10) so the parens balance;
                the rest is verbatim. */}
            <SubItem marker="i.">
              Complete the official entry form online located at{' '}
              <span className="font-bold">Flowsto.com/</span> followed by the unique
              partner identifier extension (e.g.,{' '}
              <span className="font-bold">Flowsto.com/vwgwX5gEt</span>, each a
              &ldquo;Promotion Website&rdquo;) from July 10, 2026 &ndash; August 28,
              2026
            </SubItem>
            <SubItem marker="ii.">
              The official entry form must be completely filled out, including name,
              email address and confirming you have read the official rules and are
              eligible to win the prize as outlined (&ldquo;Eligibility&rdquo;)
            </SubItem>
            <SubItem marker="iii.">Only one entry per person is allowed.</SubItem>
            <SubItem marker="iv.">
              No mechanically reproduced or photocopied entries will be accepted.
            </SubItem>
          </ol>
          <Para className="mt-[12px]">
            By completing all the steps above, you will receive one (1) entry into
            the Sweepstakes, subject to the limit below. All entries must be
            completed by 11:59 p.m. ET on August 28, 2026 to be included in this
            Sweepstakes.
          </Para>
          <Para className="mt-[12px]">
            Partially completed entry forms that contain, at a minimum, the
            entrant&rsquo;s name and address qualify as valid entries.
          </Para>
          <Para className="mt-[12px]">
            NOTE: If participating in the Sweepstakes via a mobile device, message
            and data rates may apply. Not all mobile telephone providers carry the
            service necessary to participate in this Sweepstakes. Entrants should
            consult their wireless provider&rsquo;s pricing plans.
          </Para>
          <Para className="mt-[12px]">
            The Sweepstakes Parties are not responsible for technical difficulties
            of any kind, or for unavailability of the Flow Code platform or any
            service interruption.
          </Para>
          <Para className="mt-[12px]">
            LIMIT: One (1) entry per person/email address. Attempts made by the same
            individual to earn more than the stated number of entries by using
            multiple or false contact information, email accounts or otherwise may
            result in disqualification. Entries generated by a script, computer
            programs, macro, programmed, robotic or other automated means are void
            and may be disqualified. Entries that are incomplete, illegible,
            corrupted, damaged, destroyed, forged, false, lost, late or misdirected,
            deceptive or otherwise not in compliance with the Official Rules may be
            disqualified from the Sweepstakes at Sponsor&rsquo;s sole and absolute
            discretion. Those who do not follow all instructions, provide the
            required information in their entry, or abide by these Official Rules or
            other instructions of Sponsor may be disqualified. Entries submitted by
            those who do not meet the eligibility requirements (including all
            requirements with respect to age and residence) are void. In the event
            of a dispute over the identity of an entrant, entry will be deemed
            submitted by the registered account holder of the email address that
            made such entry, provided that person is eligible. Any potential winner
            may be required to show proof of being the registered email account
            holder. Registered account holder is defined as the person assigned to
            an email address by an Internet access provider, online service provider
            or other organization responsible for assigning email addresses. In the
            event that a dispute regarding the identity of the individual who
            submitted an entry cannot be resolved to Sponsor&rsquo;s satisfaction,
            the affected entry will be deemed ineligible.
          </Para>
        </Clause>

        <Clause n={4} label="SWEEPSTAKES DRAWING:">
          On or about August 30, 2026 the Administrator will conduct a random
          drawing to select one (1) potential winners from among all eligible
          entries received in accordance with these Official Rules.
        </Clause>

        <Clause n={5} label="ODDS:">
          Odds of winning a prize depend on the total number of eligible entries
          received during the program period.
        </Clause>

        <Clause n={6} label="WINNER NOTIFICATION/PRIZE CLAIMING:">
          The potential prize winners will be notified using the email address
          information provided during entry in a commercially reasonable time after
          the applicable drawing. The Sweepstakes Parties are not responsible for
          and shall not be liable for incorrect, changed, or illegible contact
          information or for electronic communications that are undeliverable as a
          result of any form of active or passive filtering, or insufficient space
          in entrant&rsquo;s email or other account to receive messages. Sweepstakes
          Parties are not responsible for late, lost, intercepted, misdirected, or
          unsuccessful efforts to notify the potential winner. Notification is
          deemed to have occurred immediately upon sending the email. As part of the
          winner notification process, each potential winner will be required to
          complete and submit a prize redemption form (&ldquo;Redemption Form&rdquo;)
          within ten (10) days of date of notification, as a condition of receiving
          the prize. If any potential winner cannot be contacted within a reasonable
          time period or any notification is returned as undeliverable, if a
          potential winner fails or refuses to complete and submit the Redemption
          Form within the required time period, and/or if a potential winner is
          deemed ineligible or noncompliant, such potential winner will forfeit the
          prize and be disqualified, and an alternate will be selected from among all
          remaining eligible entries received until prize is awarded. A potential
          winner becomes a &ldquo;winner&rdquo; only after verification of
          eligibility by Sponsor.
          <Para className="mt-[12px]">
            No more than the stated number of prizes will be awarded. If, for any
            reason, more bona fide winners come forward seeking to claim prizes, the
            winner(s) of the advertised number of prizes available may be selected
            in a random drawing from among all persons making purportedly valid
            claims for such prize(s). Inclusion in such drawing shall be each
            entrant&rsquo;s sole and exclusive remedy under such circumstances.
          </Para>
        </Clause>

        <Clause n={7} label="PRIZES/PRIZE RESTRICTIONS:">
          The prizes to be awarded in this Sweepstakes are:
          <Para className="mt-[12px]">
            <span className="font-bold">One (1) Prize -</span> The winner will win a
            State Farm themed Secretlab TITAN Evo chair. The approximate retail value
            (&ldquo;ARV&rdquo;) of each prize is $573.
          </Para>
          <Para className="mt-[12px]">
            The total ARV of all prizes available in the Sweepstakes is $573.
          </Para>
          <Para className="mt-[12px]">
            Prizes are non-transferable, with no cash redemptions, equivalents, or
            substitutions except at Sponsor&rsquo;s sole and absolute discretion. All
            prize details not specified in these Official Rules will be determined in
            Sponsor&rsquo;s sole and absolute discretion. Prize details and
            availability are subject to change and are subject to applicable rules
            and restrictions. Prizes are awarded &ldquo;AS IS&rdquo; and without
            warranty of any kind, express or implied (including, without limitation,
            any implied warranty of merchantability or fitness for a particular
            purpose). Each winner will be solely responsible for all federal, state,
            and/or local taxes, and for any other fees or costs associated with the
            prize received, regardless of whether they, in whole or in part, are
            used. Winners will be solely responsible for complying with any tax
            reporting obligations. The ARV of the prize is based on available
            information provided to Sponsor as of the time these Official Rules are
            printed and may fluctuate. Unclaimed prizes will be forfeited. Prizes, if
            legitimately claimed, will be awarded. The Sweepstakes Parties are not
            responsible for and will not replace any lost, mutilated or stolen prize
            or any prize that is undeliverable or does not reach a winner because of
            an incorrect or changed address. If a winner does not accept or use the
            entire prize, the unaccepted or unused part of the prize will be
            forfeited, and the Sweepstakes Parties will have no further obligation
            with respect to that prize or portion of the prize. The Sweepstakes
            Parties are not responsible for, and winner will not receive the
            difference, if any, between the actual value of the prizes at the time of
            award and the stated ARV in these Official Rules or in any
            Sweepstakes-related correspondence or material. Limit one (1) prize per
            person/household. Sponsor will attempt to fulfill all properly-claimed
            prizes within approximately four (4) to eight (8) weeks after winner
            verification.
          </Para>
        </Clause>

        <Clause n={8} label="GENERAL:">
          Subject to applicable law, each winner hereby expressly grants to the
          Sweepstakes Parties and their respective successors, assigns,
          sublicensees and designees, the irrevocable right to use and publish
          his/her name, social handles, likeness (photographic or simulated), voice,
          biography and place of residence for all purposes, including, without
          limitation, advertising, marketing, promotional and publicity purposes in
          connection with this Sweepstakes (&ldquo;Advertising&rdquo;), in any and
          all media now or hereafter devised, worldwide, in perpetuity, without any
          form of notice, permission or any amount or kind of compensation, except
          for the awarding of the prize to the winner. All copyright, trademark or
          other intellectual property rights in such Advertising shall be owned by
          Sponsor and/or its licensors, and each winner hereby disclaims and waives
          any claim of right to such Advertising. Such Advertising shall be solely
          under the control of Sponsor and/or its licensors, and each winner hereby
          waives any claim of control over the Advertising content as well as any
          possible claims of misuse of winner&rsquo;s name, likeness or voice under
          contract, tort or any other theory of law. The Sweepstakes Parties do not
          assume any responsibility for any disruption in the Sweepstakes,
          including, but not limited to, the failure or interruption of any digital
          platform or any internet service provider. In the event there is a
          discrepancy or inconsistency between disclosures and other statements
          contained in any Sweepstakes materials and the terms and conditions of
          these Official Rules, these Official Rules shall prevail, govern, and
          control. In the event Sponsor is prevented from continuing with the
          Sweepstakes by any event beyond its control, including, but not limited
          to, fire, flood, epidemic, earthquake, explosion, labor dispute or strike,
          act of God or public enemy, communications or equipment failure, utility
          or service interruptions, riot or civil disturbance, terrorist threat or
          activity, war (declared or undeclared), interference with the Sweepstakes
          by any party, or any federal, state, or local government law, order, or
          regulation, order of any court or jurisdiction, or other cause not
          reasonably within Sponsor&rsquo;s control (each, a &ldquo;Force
          Majeure&rdquo; event or occurrence), Sponsor shall have the right to
          modify, suspend or terminate the Sweepstakes or prize. Sponsor
          additionally reserves the right, in its sole and absolute discretion to
          modify, suspend or terminate the Sweepstakes should causes beyond
          Sponsor&rsquo;s control corrupt or interfere with the administration,
          integrity, operation, security or proper play of the Sweepstakes.
        </Clause>

        <Clause n={9} label="CONDUCT:">
          The Sweepstakes Parties are not responsible for the actions of entrants in
          connection with the Sweepstakes, including entrants&rsquo; attempts to
          circumvent the Official Rules or otherwise interfere with the
          administration, security, fairness, integrity or proper conduct of the
          Sweepstakes. The Sweepstakes Parties reserve the right, at their sole
          discretion, to disqualify (or terminate the prize of) any individual who is
          found to be, or suspected of, acting in any manner deemed by the
          Sweepstakes Parties to be in violation of the Official Rules, or to be
          unsportsmanlike or disruptive, or with intent to annoy, abuse, threaten or
          harass any other person, or to be tampering with the entry process or the
          operation of the Sweepstakes, and void all associated entries and/or
          registrations. CAUTION: ANY ATTEMPT BY A USER, YOU OR ANY OTHER INDIVIDUAL
          TO DAMAGE OR UNDERMINE THE LEGITIMATE OPERATION OF THE SWEEPSTAKES IS A
          VIOLATION OF CRIMINAL AND CIVIL LAWS, AND SHOULD SUCH AN ATTEMPT BE MADE,
          THE SWEEPSTAKES PARTIES RESERVE THE RIGHT TO SEEK DAMAGES AND OTHER
          REMEDIES (INCLUDING ATTORNEYS&rsquo; FEES) FROM ANY SUCH PERSON TO THE
          FULLEST EXTENT PERMITTED BY LAW.
        </Clause>

        <Clause n={10} label="WAIVERS AND DISCLAIMERS:">
          The Sweepstakes Parties assume no responsibility or liability for: (a)
          lost, late, stolen, undelivered, inaccurate, incomplete, delayed,
          misdirected, damaged or garbled entries, URLs, or emails; (b) any incorrect
          or inaccurate entry information, or for any faulty or failed electronic
          data transmissions; (c) any unauthorized access to, or theft, destruction
          or alteration of entries or registrations at any point in the operation of
          this Sweepstakes; (d) any technical malfunction, failure, error, omission,
          interruption, deletion, defect, delay in operation or communications line
          failure, regardless of cause, with regard to any equipment, systems,
          networks, lines, cable, satellites, servers, computers or providers
          utilized in any aspect of the operation of the Sweepstakes; (e)
          inaccessibility or unavailability of the Internet or Flow code or any
          combination thereof or for computer hardware or software malfunctions,
          failures or difficulties, or other errors or difficulties of any kind
          whether human, mechanical, electronic, computer, network, typographical,
          printing or otherwise relating to or in connection with the Sweepstakes,
          including, without limitation, errors or difficulties which may occur in
          connection with the administration of the Sweepstakes, the processing of
          entries or registrations, the announcement of the prizes, or in any other
          Sweepstakes-related materials; or (f) any injury or damage to entrants or
          to any other person&rsquo;s computer which may be related to or resulting
          from any attempt to participate in the Sweepstakes. If, for any reason, the
          Sweepstakes (or any part thereof) is not capable of running as planned for
          reasons which may include, without limitation, infection by computer virus,
          tampering, unauthorized intervention, fraud, technical failures, or any
          other causes which may corrupt or affect the administration, security,
          fairness, integrity or proper conduct of this Sweepstakes, then the
          Sweepstakes Parties reserve the right at their sole discretion to cancel,
          terminate, modify or suspend the Sweepstakes in whole or in part. If
          terminated, the Sweepstakes Parties will award the prize in a random
          drawing from among all non-suspect, eligible entries received for the
          Sweepstakes up to the time of such action.
        </Clause>

        <Clause n={11} label="RELEASES:">
          All entrants, as a condition of participation in this Sweepstakes, release,
          discharge, indemnify and hold harmless the Sweepstakes Parties, Flow Code,
          and each of their respective directors, officers, employees, agents,
          successors and assigns (collectively, &ldquo;Released Parties&rdquo;) from
          and against any and all liability, claims, costs (including
          attorneys&rsquo; fees), losses, damages, fines, or actions of any kind
          whatsoever for injuries, death, damages, or losses to persons or property
          which may be sustained, in whole or in part, directly or indirectly, in
          connection with: (i) participation in any aspect of the Sweepstakes
          (including travel to/from any Sweepstakes activity), (ii) the receipt,
          ownership, use or misuse of the prize awarded, including any travel
          associated with any prize, (iii) the Released Parties&rsquo; violation of
          rights of publicity or privacy, claims of defamation or portrayal in a
          false light or based on any claim of infringement of intellectual property;
          (iv) entrant&rsquo;s registration material on any related website, or (v)
          any typographical, human or other error in the printing, offering,
          selection, operation or announcement of any Sweepstakes activity and/or
          prize.
        </Clause>

        <Clause n={12} label="GOVERNING LAW AND LIMITATION OF LIABILITY:">
          All issues and questions concerning the construction, validity,
          interpretation and enforceability of these Official Rules or the rights and
          obligations of entrants, Sponsor or the Released Parties in connection with
          the Sweepstakes will be governed by and construed in accordance with the
          internal laws of the State of Illinois, without giving effect to any choice
          of law or conflict of law rules or provisions that would cause the
          application of any other laws.
          <Para className="mt-[12px]">
            BY ENTERING THE SWEEPSTAKES, ENTRANT AGREES THAT TO THE EXTENT PERMITTED
            BY APPLICABLE LAW: (A) ANY AND ALL DISPUTES, CLAIMS AND CAUSES OF ACTION
            ARISING OUT OF OR CONNECTED WITH THE SWEEPSTAKES, OR ANY PRIZE AWARDED,
            WILL BE RESOLVED INDIVIDUALLY, WITHOUT RESORT TO ANY FORM OF CLASS ACTION;
            (B) ANY AND ALL CLAIMS, JUDGMENTS AND AWARDS WILL BE LIMITED TO ACTUAL
            THIRD-PARTY, OUT-OF-POCKET COSTS INCURRED (IF ANY) NOT TO EXCEED TEN
            DOLLARS ($10.00), BUT IN NO EVENT WILL ATTORNEYS&rsquo; FEES BE AWARDED OR
            RECOVERABLE; (C) UNDER NO CIRCUMSTANCES WILL ANY ENTRANT BE PERMITTED TO
            OBTAIN ANY AWARD FOR, AND ENTRANT HEREBY KNOWINGLY AND EXPRESSLY WAIVES
            ALL RIGHTS TO SEEK, PUNITIVE, INCIDENTAL, CONSEQUENTIAL OR SPECIAL
            DAMAGES, LOST PROFITS AND/OR ANY OTHER DAMAGES, OTHER THAN ACTUAL OUT OF
            POCKET EXPENSES NOT TO EXCEED TEN DOLLARS ($10.00), AND/OR ANY RIGHTS TO
            HAVE DAMAGES MULTIPLIED OR OTHERWISE INCREASED; AND (D) ENTRANTS&rsquo;
            REMEDIES ARE LIMITED TO A CLAIM FOR MONEY DAMAGES (IF ANY) AND ENTRANT
            IRREVOCABLY WAIVES ANY RIGHT TO SEEK INJUNCTIVE OR EQUITABLE RELIEF. SOME
            JURISDICTIONS DO NOT ALLOW THE LIMITATIONS OR EXCLUSION OF LIABILITY, SO
            THE ABOVE MAY NOT APPLY TO YOU.
          </Para>
        </Clause>

        <Clause n={13} label="DISPUTE RESOLUTION:">
          The parties each agree to finally settle all disputes only through
          arbitration; provided, however, the Sponsor shall be entitled to seek
          injunctive or equitable relief in the state and federal courts in the State
          of Illinois, and any other court with jurisdiction over the parties. In
          arbitration, there is no judge or jury, and review is limited. The
          arbitrator&rsquo;s decision and award is final and binding, with limited
          exceptions, and judgment on the award may be entered in any court with
          jurisdiction. The parties agree that, except as set forth above, any claim,
          suit, action or proceeding arising out of or relating to this Sweepstakes
          shall be resolved solely by binding arbitration before a sole arbitrator
          under the streamlined Arbitration Rules Procedures of JAMS Inc.
          (&ldquo;JAMS&rdquo;) or any successor to JAMS. In the event JAMS is
          unwilling or unable to set a hearing date within fourteen (14) days of the
          filing of a &ldquo;Demand for Arbitration&rdquo;, then either party can
          elect to have the arbitration administered by the American Arbitration
          Association (&ldquo;AAA&rdquo;) or any other mutually agreeable arbitration
          administration service. If an in-person hearing is required, then it will
          take place in the State of Illinois. The federal or state law that applies
          to these Official Rules will also apply during the arbitration. Disputes
          will be arbitrated only on an individual basis and will not be consolidated
          with any other proceedings that involve any claims or controversy of
          another party, including any class actions; provided, however, if for any
          reason any court or arbitrator holds that this restriction is
          unconscionable or unenforceable, then the agreement to arbitrate
          doesn&rsquo;t apply and the dispute must be brought in a court of competent
          jurisdiction in the State of Illinois. Sponsor agrees to pay the
          administrative and arbitrator&rsquo;s fees in order to conduct the
          arbitration (but specifically excluding any travel or other costs of
          entrant to attend the arbitration hearing). Either party may,
          notwithstanding this provision, bring qualifying claims in small claims
          court.
        </Clause>

        <Clause n={14} label="ENTRY INFORMATION AND SWEEPSTAKES COMMUNICATIONS:">
          As a condition of entering the Sweepstakes, each entrant gives consent for
          Sponsor to obtain and deliver his or her name, address and other
          information to third parties for the purpose of administering this
          Sweepstakes and to comply with applicable laws, regulations and rules. Any
          information entrant provides to Sponsor may be used to communicate with
          entrant in relation to this Sweepstakes or on a Sweepstakes winner&rsquo;s
          list. By participating in the Sweepstakes, entrant agrees to all of the
          terms and conditions of the Sponsor&rsquo;s Privacy Policy, which is
          available at{' '}
          <a
            href="https://www.statefarm.com/customer-care/privacy-security/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-[#00ffb2]"
          >
            https://www.statefarm.com/customer-care/privacy-security/privacy
          </a>
          . In the event of any discrepancy between the Sponsor&rsquo;s Privacy
          Policy and these Official Rules, these Official Rules shall control and
          govern.
        </Clause>

        <Clause n={15} label="MISCELLANEOUS:">
          The invalidity or unenforceability of any provision of these Official Rules
          will not affect the validity or enforceability of any other provision. In
          the event that any provision of the Official Rules is determined to be
          invalid or otherwise unenforceable or illegal, the other provisions will
          remain in effect and will be construed in accordance with their terms as if
          the invalid or illegal provision were not contained herein. Sponsor&rsquo;s
          failure to enforce any term of these Official Rules will not constitute a
          waiver of that provision. Entrants agree to waive any rights to claim
          ambiguity of these Official Rules. Headings are solely for convenience of
          reference and will not be deemed to affect in any manner the meaning or
          intent of the documents or any provision hereof. In the event there is a
          discrepancy or inconsistency between disclosures or other statements
          contained in any Sweepstakes-related materials, privacy policy or terms of
          use on any website, social media platform or application and/or the terms
          and conditions of the Official Rules, the Official Rules shall prevail,
          govern and control and the discrepancy will be resolved in Sponsor&rsquo;s
          sole and absolute discretion.
        </Clause>

        {/* §16: the 07.09.26 v2 AND v3 documents both print "State Farm Code
            Gamerhood Chair Giveaway" here — a source typo ("Flow" dropped but
            "Code" left when the giveaway was renamed from "…Flow Code Gamerhood…").
            Corrected to the giveaway's actual name per client direction (GHS5-80,
            Jeana Newsom 2026-07-09: "Please remove code"). The rest of §16 is
            verbatim. */}
        <Clause n={16} label="WINNERS LIST:">
          To receive a list of the names of the winners, send a #10 self-addressed,
          stamped envelope for receipt by October 1, 2026 to: State Farm Gamerhood
          Chair Giveaway - Winners List Request, c/o The Marketing Arm, 7850 N Belt
          Line Rd, 5th Floor, Irving, TX 75063
        </Clause>

        <Clause n={17} label="SPONSOR:">
          State Farm Mutual Automobile Insurance Company, One State Farm Plaza,
          Bloomington, IL 61710. Reference to third parties in connection with the
          prizes and/or third-party websites or services are for reference and
          identification purposes only and not intended to suggest endorsement,
          sponsorship or affiliation with Sponsor or the Sweepstakes. State Farm is in
          the business of insurance and financial products and services.
        </Clause>

        <Clause n={18} label="ADMINISTRATOR:">
          The Marketing Arm (TMA), 7850 N Belt Line Rd, 5th Floor, Irving, TX 75063
        </Clause>
      </ol>
    </div>
  );
}
